import { useMemo } from "react";
import type { Invoice, HaulierLineItem, WarehouseLineItem } from "../types";
import {
  InvoiceType,
  HaulierLineStatus,
  WarehouseLineStatus,
} from "../types";

// ─── Types ──────────────────────────────────────────────────────────────────

interface DiscrepancyReportProps {
  invoice: Invoice;
  haulierLines?: HaulierLineItem[];
  warehouseLines?: WarehouseLineItem[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const gbp = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
});

function formatGBP(amount: number): string {
  return gbp.format(amount);
}

function formatDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

const HAULIER_ISSUE_STATUSES = new Set<string>([
  HaulierLineStatus.OVERCHARGED,
  HaulierLineStatus.UNDERCHARGED,
  HaulierLineStatus.RATE_NOT_FOUND,
  HaulierLineStatus.UNKNOWN_SERVICE,
]);

const WAREHOUSE_ISSUE_STATUSES = new Set<string>([
  WarehouseLineStatus.OVERCHARGED,
  WarehouseLineStatus.UNDERCHARGED,
]);

const STATUS_LABELS: Record<string, string> = {
  OVERCHARGED: "Overcharged",
  UNDERCHARGED: "Undercharged",
  RATE_NOT_FOUND: "Rate not found",
  UNKNOWN_SERVICE: "Unknown service",
};

function issueColor(status: string): string {
  if (status === "OVERCHARGED") return "text-red-600";
  if (status === "UNDERCHARGED") return "text-amber-600";
  return "text-gray-700";
}

function differenceColor(diff: number): string {
  if (diff > 0) return "text-red-600 font-semibold";
  if (diff < 0) return "text-amber-600 font-semibold";
  return "";
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function DiscrepancyReport({
  invoice,
  haulierLines = [],
  warehouseLines = [],
}: DiscrepancyReportProps) {
  const isHaulier = invoice.invoice_type === InvoiceType.HAULIER;

  const haulierIssues = useMemo(
    () => haulierLines.filter((l) => HAULIER_ISSUE_STATUSES.has(l.status)),
    [haulierLines],
  );

  const warehouseIssues = useMemo(
    () => warehouseLines.filter((l) => WAREHOUSE_ISSUE_STATUSES.has(l.status)),
    [warehouseLines],
  );

  const hasIssues = isHaulier
    ? haulierIssues.length > 0
    : warehouseIssues.length > 0;

  // Don't render if there are no discrepancy lines
  if (!hasIssues) return null;

  // ─── Aggregate totals ───────────────────────────────────────────────────

  const totals = useMemo(() => {
    if (isHaulier) {
      const totalInvoiced = haulierIssues.reduce(
        (sum, l) => sum + l.invoiced_amount,
        0,
      );
      const totalExpected = haulierIssues.reduce(
        (sum, l) => sum + l.expected_amount,
        0,
      );
      const overchargedCount = haulierIssues.filter(
        (l) => l.status === HaulierLineStatus.OVERCHARGED,
      ).length;
      const underchargedCount = haulierIssues.filter(
        (l) => l.status === HaulierLineStatus.UNDERCHARGED,
      ).length;
      const rateNotFoundCount = haulierIssues.filter(
        (l) => l.status === HaulierLineStatus.RATE_NOT_FOUND,
      ).length;
      const unknownServiceCount = haulierIssues.filter(
        (l) => l.status === HaulierLineStatus.UNKNOWN_SERVICE,
      ).length;
      return {
        totalInvoiced,
        totalExpected,
        netOvercharge: totalInvoiced - totalExpected,
        issueCount: haulierIssues.length,
        overchargedCount,
        underchargedCount,
        rateNotFoundCount,
        unknownServiceCount,
      };
    }

    const totalInvoiced = warehouseIssues.reduce(
      (sum, l) => sum + l.invoiced_price,
      0,
    );
    const totalExpected = warehouseIssues.reduce(
      (sum, l) => sum + l.expected_price,
      0,
    );
    const overchargedCount = warehouseIssues.filter(
      (l) => l.status === WarehouseLineStatus.OVERCHARGED,
    ).length;
    const underchargedCount = warehouseIssues.filter(
      (l) => l.status === WarehouseLineStatus.UNDERCHARGED,
    ).length;
    return {
      totalInvoiced,
      totalExpected,
      netOvercharge: totalInvoiced - totalExpected,
      issueCount: warehouseIssues.length,
      overchargedCount,
      underchargedCount,
      rateNotFoundCount: 0,
      unknownServiceCount: 0,
    };
  }, [isHaulier, haulierIssues, warehouseIssues]);

  const totalLinesChecked = isHaulier
    ? haulierLines.length
    : warehouseLines.length;

  // ─── Breakdown text ─────────────────────────────────────────────────────

  const breakdownParts: string[] = [];
  if (totals.overchargedCount > 0)
    breakdownParts.push(`${totals.overchargedCount} overcharged`);
  if (totals.underchargedCount > 0)
    breakdownParts.push(`${totals.underchargedCount} undercharged`);
  if (totals.rateNotFoundCount > 0)
    breakdownParts.push(`${totals.rateNotFoundCount} rate not found`);
  if (totals.unknownServiceCount > 0)
    breakdownParts.push(`${totals.unknownServiceCount} unknown service`);
  const breakdownText = breakdownParts.join(", ");

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-md print:shadow-none print:border print:border-gray-300">
      {/* ── Report Header ──────────────────────────────────────────────── */}
      <div className="border-b border-gray-200 px-8 py-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Pricing Discrepancy Report
            </h2>
            <div className="mt-3 space-y-1 text-sm text-gray-600">
              <p>
                <span className="font-medium text-gray-700">
                  Invoice Number:
                </span>{" "}
                {invoice.invoice_number}
              </p>
              <p>
                <span className="font-medium text-gray-700">Invoice Date:</span>{" "}
                {formatDate(invoice.invoice_date)}
              </p>
              <p>
                <span className="font-medium text-gray-700">Supplier:</span>{" "}
                {invoice.supplier}
              </p>
            </div>
          </div>
          <div className="text-right">
            <button
              onClick={() => window.print()}
              className="mb-4 inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 print:hidden"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18.75 12h.008v.008h-.008V12Zm-12 0h.008v.008H6.75V12Z"
                />
              </svg>
              Print Report
            </button>
            <div className="space-y-1 text-sm text-gray-600">
              <p>
                <span className="font-medium text-gray-700">Prepared by:</span>{" "}
                Zebra Renewables
              </p>
              <p>
                <span className="font-medium text-gray-700">Date:</span>{" "}
                {formatDate(new Date().toISOString())}
              </p>
            </div>
          </div>
        </div>

        {/* Quick stats row */}
        <div className="mt-5 flex gap-6 rounded-md bg-gray-50 px-4 py-3 text-sm">
          <div>
            <span className="text-gray-500">Lines checked:</span>{" "}
            <span className="font-semibold text-gray-900">
              {totalLinesChecked}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Lines with issues:</span>{" "}
            <span className="font-semibold text-red-600">
              {totals.issueCount}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Total overcharge:</span>{" "}
            <span
              className={`font-semibold ${totals.netOvercharge > 0 ? "text-red-600" : "text-gray-900"}`}
            >
              {formatGBP(totals.netOvercharge)}
            </span>
          </div>
        </div>
      </div>

      {/* ── Discrepancy Table ──────────────────────────────────────────── */}
      <div className="overflow-x-auto px-8 py-6">
        {isHaulier ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-300 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                <th className="pb-2 pr-3">Date</th>
                <th className="pb-2 pr-3">Con No</th>
                <th className="pb-2 pr-3">Destination</th>
                <th className="pb-2 pr-3">Postcode</th>
                <th className="pb-2 pr-3">Service</th>
                <th className="pb-2 pr-3">Pallet Spec</th>
                <th className="pb-2 pr-3 text-right">Invoiced</th>
                <th className="pb-2 pr-3 text-right">Expected</th>
                <th className="pb-2 pr-3 text-right">Difference</th>
                <th className="pb-2">Issue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {haulierIssues.map((line, idx) => (
                <tr
                  key={line.id}
                  className={idx % 2 === 1 ? "bg-gray-50" : "bg-white"}
                >
                  <td className="whitespace-nowrap py-2 pr-3">
                    {formatDate(line.delivery_date)}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3 font-mono text-xs">
                    {line.consignment_number}
                  </td>
                  <td className="py-2 pr-3">{line.destination_name}</td>
                  <td className="whitespace-nowrap py-2 pr-3">
                    {line.destination_postcode}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3">
                    {line.service_type}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3">
                    {line.pallet_spec}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3 text-right font-mono">
                    {formatGBP(line.invoiced_amount)}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3 text-right font-mono">
                    {formatGBP(line.expected_amount)}
                  </td>
                  <td
                    className={`whitespace-nowrap py-2 pr-3 text-right font-mono ${differenceColor(line.amount_difference)}`}
                  >
                    {formatGBP(line.amount_difference)}
                  </td>
                  <td
                    className={`whitespace-nowrap py-2 ${issueColor(line.status)}`}
                  >
                    {STATUS_LABELS[line.status] ?? line.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-300 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                <th className="pb-2 pr-3">Date</th>
                <th className="pb-2 pr-3">Charge Type</th>
                <th className="pb-2 pr-3">Code</th>
                <th className="pb-2 pr-3">Product</th>
                <th className="pb-2 pr-3 text-right">Invoiced</th>
                <th className="pb-2 pr-3 text-right">Expected</th>
                <th className="pb-2 pr-3 text-right">Difference</th>
                <th className="pb-2">Issue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {warehouseIssues.map((line, idx) => (
                <tr
                  key={line.id}
                  className={idx % 2 === 1 ? "bg-gray-50" : "bg-white"}
                >
                  <td className="whitespace-nowrap py-2 pr-3">
                    {formatDate(line.created_at)}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3">
                    {line.charge_type}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3 font-mono text-xs">
                    {line.charge_code}
                  </td>
                  <td className="py-2 pr-3">{line.product_code}</td>
                  <td className="whitespace-nowrap py-2 pr-3 text-right font-mono">
                    {formatGBP(line.invoiced_price)}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3 text-right font-mono">
                    {formatGBP(line.expected_price)}
                  </td>
                  <td
                    className={`whitespace-nowrap py-2 pr-3 text-right font-mono ${differenceColor(line.price_difference)}`}
                  >
                    {formatGBP(line.price_difference)}
                  </td>
                  <td
                    className={`whitespace-nowrap py-2 ${issueColor(line.status)}`}
                  >
                    {STATUS_LABELS[line.status] ?? line.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Summary Footer ─────────────────────────────────────────────── */}
      <div className="border-t border-gray-200 px-8 py-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="space-y-2 text-sm">
            <div className="flex gap-8">
              <p>
                <span className="text-gray-500">Total Invoiced:</span>{" "}
                <span className="font-semibold text-gray-900">
                  {formatGBP(totals.totalInvoiced)}
                </span>
              </p>
              <p>
                <span className="text-gray-500">Total Expected:</span>{" "}
                <span className="font-semibold text-gray-900">
                  {formatGBP(totals.totalExpected)}
                </span>
              </p>
            </div>
            <p className="text-xs text-gray-400">{breakdownText}</p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wider text-gray-500">
              Net Overcharge
            </p>
            <p
              className={`text-2xl font-bold ${totals.netOvercharge > 0 ? "text-red-600" : "text-gray-900"}`}
            >
              {formatGBP(totals.netOvercharge)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
